export default {
    common: {
        loading: "Loading…",
        back: "Back",
        save: "Save",
        cancel: "Cancel",
        close: "Close",
        viewAll: "View all",
    },
    nav: {
        home: "Home",
        shop: "Shop",
        bulkQuote: "Bulk quote",
        faq: "FAQ",
        research: "Research",
        about: "About",
        contact: "Contact",
    },
    header: {
        signIn: "Sign in",
        account: "Account",
        admin: "Admin",
        logout: "Logout",
        openCart: "Open cart",
        menu: "Menu",
        language: "Language",
    },
    footer: {
        disclaimerTitle: "Research use only.",
        disclaimer:
            "All peptides listed on this site are sold strictly for in-vitro laboratory research purposes and are not for human or veterinary consumption. They are not intended to diagnose, treat, cure, or prevent any disease and should not be ingested, injected, applied, or otherwise consumed by humans or animals.",
        readFullDisclaimer: "Read the full disclaimer →",
        tagline:
            "High-purity research peptides, synthesised at our partner lab. Strictly for laboratory and research use only. Not for human consumption.",
        purityBadge: "≥99% verified purity · HPLC tested",
        company: "Company",
        legal: "Legal",
        about: "About",
        research: "Research",
        shop: "Shop",
        contact: "Contact",
        bulkQuote: "Bulk quote",
        trackOrder: "Track order",
        faq: "FAQ",
        privacy: "Privacy policy",
        gdpr: "GDPR policy",
        cookies: "Cookie policy",
        terms: "Terms & conditions",
        complaints: "Complaints procedure",
        copyright: "© {{year}} Nexus Peptides. All rights reserved.",
        status: "SYS.STATUS / ONLINE · CRYO.BAY / 02 · LOT.OK",
    },
    hero: {
        badge: "Research grade · ≥99% HPLC verified",
        headlineLine1: "The molecular",
        headlineLine2: "edge of",
        headlineAccent: "modern",
        headlineLine3: "research.",
        body:
            "High-purity peptides synthesised at our partner lab, third-party HPLC and mass-spec verified. Engineered for laboratories who refuse to compromise on data integrity.",
        searchLabel: "Product search",
        searchPlaceholder: "Search 50+ peptides by name or what they do…",
        searchButton: "Search",
        exploreCatalog: "Explore catalog",
        viewResearch: "View research",
        statPurity: "HPLC purity",
        statCompounds: "compounds",
        statColdChain: "cold chain",
        dragHint: "DRAG · ROTATE · EXPLORE",
        tickerGuideLabel: "FREE: NEXUS PEPTIDES 101 GUIDE — DOWNLOAD PDF",
        sectionCatalog: "// Catalog · 01",
        signature: "Signature compounds",
        viewFullCatalog: "View full catalog",
        sectionMethod: "// Method · 02",
        methodHeading1: "Engineered for",
        methodHeading2: "data you can",
        methodAccent: "trust",
        methodBody:
            "Every batch passes a 6-stage QC protocol at our partner lab. From raw amino acid synthesis through HPLC, LC-MS, and microbial assay — they publish the data so yours has a foundation.",
        partnerLabSpec: "Partner-lab spec",
        partnerLabSpecCaption: "Third-party HPLC and mass-spec verified per batch",
        ctaEyebrow: "// Ready to research",
        ctaHeading: "Source the compounds your protocol deserves.",
        ctaBrowse: "Browse catalog",
        ctaBulk: "Bulk inquiry",
    },
    chat: {
        title: "Nexus concierge",
        online: "Online · Instant reply",
        toggle: "Open chat",
        send: "Send",
        placeholder: "Ask about a compound, COA, lead time…",
        disclaimer: "AI assistant · research-only guidance",
        fallbackGreeting: "Hi — how can I help with your research supply today?",
        rateLimited: "You've reached the daily chat limit. Please try again tomorrow or use 'Ask a human' below.",
        errorGeneric: "The assistant is temporarily unavailable. Please try again shortly.",
        back: "Back",
        askHuman: "Ask a human",
        startersEyebrow: "Quick starts",
        starters: {
            bulkPricing: {
                label: "Bulk pricing",
                hint: "Volume tiers & institutional accounts",
                prompt: "What are your volume-pricing tiers for a bulk order?",
            },
            leadTimes: {
                label: "Lead times",
                hint: "Standard, priority & custom batches",
                prompt: "What are your typical lead times for standard and priority orders?",
            },
            coas: {
                label: "COAs",
                hint: "Certificate of Analysis per lot",
                prompt: "How do I get a Certificate of Analysis for a product I'm about to order?",
            },
        },
        handoffTitle: "Ask a human",
        handoffSubtitle: "We reply within 1 business day",
        handoffIntro: "Leave your email and a short note — a human on our team will reply with your full chat transcript attached.",
        handoffEmail: "Work email *",
        handoffName: "Your name (optional)",
        handoffNote: "Anything specific you'd like us to answer? (optional)",
        handoffSubmit: "Send to the team",
        handoffSending: "Sending…",
        handoffDone: "Sent — talk soon",
        handoffDoneBody: "We'll reply to your email within one business day.",
        handoffFooter: "Your chat transcript will be attached automatically.",
        handoffError: "Couldn't send. Please try again in a moment.",
    },

    loyalty: {
        eyebrow: "// Loyalty program",
        pts: "pts",
        worth: "Worth £{{amount}} at checkout",
        hint: "Earn {{earn}} points on every £1 spent. {{value}}p per point when you redeem. Up to {{pct}}% off any order.",
        redeemLabel: "Redeem loyalty points",
        redeemHint: "You have {{balance}} pts · max {{max}} on this order ({{pct}}% cap)",
        redeemNone: "Clear",
        redeemMax: "Use max",
        rowLabel: "Loyalty redemption ({{pts}} pts)",
        earnMultiplier: "earn rate",
        progressTo: "Progress to {{label}}",
        perks: {
            bronze: "",
            silver: "Silver perk: free UK shipping on every order",
            gold: "Gold perks: free UK + EU shipping + 1.25× point earning",
            platinum: "Platinum perks: free worldwide shipping + 1.5× point earning",
        },
    },

    process: {
        synthesis: {
            title: "Solid-phase synthesis",
            desc: "Fmoc-protected Merrifield protocol on automated peptide synthesizers.",
        },
        hplc: {
            title: "HPLC + LC-MS",
            desc: "Reverse-phase HPLC at 220nm and 280nm; mass confirmed to ±1 Da.",
        },
        screening: {
            title: "Microbial screening",
            desc: "Endotoxin and bioburden screening per batch.",
        },
        coldchain: {
            title: "Cold chain logistics",
            desc: "−20°C lyophilized storage; insulated thermal shipping containers.",
        },
    },
    shop: {
        eyebrow: "// The catalog",
        title: "Research compounds",
        intro:
            "Every vial backed by a Certificate of Analysis. Filter by category, or browse the complete library. All products ship lyophilized in 10mL borosilicate vials with rubber septa.",
        searchLabel: "Product search",
        searchPlaceholder: "Search peptides by name, category, or what they do…",
        clear: "Clear",
        filterAll: "All",
        resultsCount: "Showing {{count}} of {{total}} compounds{{queryPart}}",
        resultsQueryPart: " matching “{{query}}”",
        loading: "Loading catalog…",
        noMatchesTitle: "No matches",
        noMatchesBody: "Nothing found for “{{query}}” in {{where}}.",
        noMatchesWhereAll: "the catalog",
        resetFilters: "Reset filters",
        requestTitle: "Can't find what you need?",
        requestBody: "Our catalog is curated, but we can source almost any research peptide on request. Let us know what you're looking for.",
        requestCta: "Request a peptide",
    },
    product: {
        purity: "Purity",
        labPick: "Top pick",
        penBadge: "Pen format",
        from: "from",
        add: "Add",
        whatItDoes: "What it does",
        selectSize: "Select size",
        availableSizes: "Available sizes",
        addToCart: "Add",
        researchAdvisory: "Research advisory",
        doses: "Doses",
        sizes: "{{count}} sizes",
        classLabel: "Class",
        hplc: "HPLC",
        perVial: "/ vial",
        vialOf: "{{dose}} vial",
        tabOverview: "Overview",
        tabSpecs: "Specs",
        tabStorage: "Storage",
        highlights: "Research highlights",
        sequence: "Sequence",
        molecularWeight: "Molecular weight",
        format: "Format",
        storageBody:
            "Once reconstituted with bacteriostatic water, store at 2–8°C and use within 14 days. Avoid repeated freeze-thaw cycles. Strictly for in-vitro research and not for human or veterinary use.",
        crumbHome: "Home",
        crumbShop: "Shop",
        notFound: "Product not found",
        backToShop: "← Back to shop",
        coaAvailable: "COA available",
        downloadCoa: "Download COA (PDF)",
        coldChain: "Cold chain",
        researchOnly: "Research only",
        viewPhoto: "Photo",
        view3D: "3D model",
        residues: "residues",
        dragRotate: "Drag · rotate",
        structureUnavailableEyebrow: "Structure unavailable",
        structureUnavailableTitle: "3D model unavailable for this compound",
        structureUnavailableBody: "This compound doesn't have a peptide sequence we can render in 3D. Switch back to the photo view for product imagery.",
    },
    cart: {
        eyebrow: "Your cart",
        countOne: "1 item",
        countOther: "{{count}} items",
        emptyTitle: "Empty",
        emptyBody: "No compounds in your cart yet.",
        each: "ea",
        subtotal: "Subtotal",
        bundleApplied: "Bundle −{{pct}}%",
        bundleNudge: "Add 1 more for −{{pct}}%",
        bundleSavings: "Bundle savings",
        freeShipping: "Free shipping unlocked",
        towardFreeShipping: "£{{amount}} away from free shipping",
        tierCountdown: "You're {{points}} pts from {{tier}}",
        tierTopReached: "You've reached {{tier}} — our top tier",
        checkout: "Proceed to checkout →",
        keepBrowsing: "Continue browsing",
        requestBulkQuote: "Request a bulk quote instead",
        remove: "Remove",
    },
    checkout: {
        eyebrow: "// Secure checkout",
        title: "Finalize order",
        sectionShipping: "Shipping details",
        sectionPayment: "Payment",
        demoMode: "Demo mode",
        demoNote:
            "This is a mock payment gateway. No real card will be charged. Use any 16-digit number to test (e.g. 4242 4242 4242 4242).",
        fullName: "Full name",
        email: "Email",
        address: "Address",
        city: "City",
        state: "State",
        zip: "ZIP",
        country: "Country",
        phone: "Phone",
        cardNumber: "Card number",
        expiry: "Expiry (MM/YY)",
        cvc: "CVC",
        summaryEyebrow: "// Order summary",
        sumBundle: "Cart total",
        sumBundleSavings: "Bundle discount",
        sumSubtotal: "Subtotal",
        sumShipping: "Shipping",
        sumShippingFree: "Free",
        shipPolicyLabel: "Shipping:",
        shipPolicyBody: "Free worldwide shipping on orders over £{{threshold}}. Under that, £15 UK · £20 EU · £30 rest of world.",
        shipAwayFromFree: "£{{amount}} more for free shipping",
        sumTax: "Tax (est.)",
        sumTotal: "Total",
        place: "Place order",
        placing: "Processing…",
        secureNote: "256-bit encrypted · Demo only",
        emptyTitle: "Cart empty",
        emptyBody: "Add a compound to begin checkout.",
        browseCatalog: "Browse catalog",
        qty: "Qty {{n}}",
    },
    shipBanner: {
        free: "Free worldwide shipping on orders over £150",
        regions: "£15 UK · £20 EU · £30 Rest of World",
        request: "Can't find a peptide? Request it — we source almost anything",
        progress: "£{{amount}} away from free UK shipping",
        earned: "Free UK shipping unlocked",
    },
    order: {
        confirmedEyebrow: "// Confirmed",
        title: "Order placed",
        reference: "Reference",
        shippingTo: "Shipping to",
        payment: "Payment",
        status: "Status",
        subtotal: "Subtotal",
        shipping: "Shipping",
        shippingFree: "Free",
        tax: "Tax",
        totalPaid: "Total paid",
        coldChainTitle: "Cold-chain dispatch in 24h",
        coldChainBody:
            "Your order ships in insulated thermal packaging from our −20°C cryo bay. A tracking number will be emailed within 24 hours.",
        continueBrowsing: "Continue browsing",
        viewMyOrders: "View my orders",
        each: "each",
    },
    trackOrder: {
        eyebrow: "// Order tracking",
        title: "Track your order",
        subtitle: "Enter your order reference and the email used at checkout to see live status.",
        refLabel: "Order reference",
        refPlaceholder: "NXP-XXXXXXXX",
        emailLabel: "Email",
        emailPlaceholder: "you@example.com",
        submit: "Track order",
        submitting: "Looking up…",
        notFound: "No order found matching that reference and email. Check both and try again.",
        placedOn: "Placed on {{date}}",
        itemsTitle: "Items",
        total: "Total",
        cancelled: "This order was cancelled.",
        stepPending: "Pending",
        stepConfirmed: "Confirmed",
        stepProcessing: "Processing",
        stepShipped: "Shipped",
        stepDelivered: "Delivered",
        another: "Track another order",
    },
    auth: {
        loginEyebrow: "// Sign in",
        loginTitle: "Researcher login",
        loginWelcomeEyebrow: "// Welcome back",
        loginWelcomeLine1: "Access the",
        loginWelcomeLine2: "cryo bay",
        loginWelcomeBody:
            "Sign in to track orders, download Certificates of Analysis, and access researcher-tier pricing.",
        emailLabel: "Email",
        passwordLabel: "Password",
        submitLogin: "Sign in",
        submittingLogin: "Authenticating…",
        newHere: "New here?",
        createAccount: "Create an account →",
        registerEyebrow: "// Create account",
        registerTitle: "Researcher signup",
        nameLabel: "Full name",
        passwordMin: "Password (≥6 chars)",
        submitRegister: "Create account",
        submittingRegister: "Creating account…",
        alreadyRegistered: "Already registered?",
        signInLink: "Sign in →",
        registerConfirm:
            "By signing up you confirm you are a qualified researcher and acknowledge that all compounds are sold strictly for in-vitro research use only — not for human or veterinary consumption.",
        memberBenefitsEyebrow: "// Member benefits",
        memberBenefitsLine1: "Join the",
        memberBenefitsLine2: "network",
        memberBenefits: [
            "· Order history & COA archive",
            "· Researcher-tier pricing (-15%)",
            "· Priority cold-chain dispatch",
            "· Early access to new compounds",
        ],
        accountEyebrow: "// Researcher dashboard",
        hello: "Hello, {{name}}",
        signOut: "Sign out",
        ordersCount: "Orders",
        statStatus: "Status",
        statSince: "Member since",
        admin: "Admin",
        verified: "Verified",
        orderHistory: "Order history",
        noOrders: "No orders yet.",
        rowReference: "Reference",
        reorder: "Reorder",
        reordering: "Adding…",
        reorderError: "Couldn't reorder — please try again.",
        viewOrder: "View order",
        rowDate: "Date",
        rowItems: "Items",
        rowTotal: "Total",
    },
    faq: {
        eyebrow: "// Frequently asked",
        titleLine1: "Frequently",
        titleLine2: "asked questions",
        intro:
            "Answers to the most common questions about ordering, shipping, purity certification, and our research-use policy.",
        stillEyebrow: "// Still have questions?",
        stillTitle: "Talk to our partner lab's technical team",
        stillBody:
            "For compound-specific or order-related queries we usually respond within one business day.",
        contactUs: "Contact us",
        items: [
            {
                q: "What are Nexus Peptides products used for?",
                a: [
                    "Every product listed on this site is sold strictly for in-vitro laboratory research purposes only. Our compounds are intended for use by qualified researchers in a controlled laboratory environment and are not for human or veterinary consumption. They are not intended to diagnose, treat, cure, or prevent any disease.",
                ],
            },
            {
                q: "Do you provide a Certificate of Analysis (COA) with every order?",
                a: [
                    "Yes. Every batch is independently HPLC and mass-spectrometry verified, and a full Certificate of Analysis is available for each lot. Signed-in researcher accounts can access the COA archive alongside their order history.",
                ],
            },
            {
                q: "How pure are your compounds?",
                a: [
                    "All compounds are synthesised at our partner lab and released only after passing a 6-stage QC protocol. Median verified purity is ≥99% by reverse-phase HPLC at 220 nm and 280 nm, with mass confirmed by LC-MS.",
                ],
            },
            {
                q: "How are orders shipped and stored in transit?",
                a: [
                    "Products ship lyophilised in borosilicate vials from our −20°C cryo bay, packed in insulated thermal containers with cold-chain monitoring. Dispatch is within 24 hours of order confirmation and a tracking number is emailed as soon as the parcel is collected by the courier.",
                ],
            },
            {
                q: "What are your shipping rates?",
                a: [
                    "Free worldwide shipping on orders over £150. Below that: £15 UK, £20 EU, and £30 rest of world. Duties and import taxes are the recipient's responsibility where applicable.",
                ],
            },
            {
                q: "How should compounds be stored on arrival?",
                a: [
                    "Store lyophilised vials at −20°C protected from light. Handling should follow standard laboratory practice for research reagents. Always consult the technical data sheet for the specific compound before use in any research protocol.",
                ],
            },
            {
                q: "Can I request a compound that isn't in the catalog?",
                a: [
                    "Yes. Our catalog is curated but our partner lab can source or synthesise almost any research peptide on request. Use the contact form to describe what you need — bulk and custom requests receive a quotation within one business day.",
                ],
            },
        ],
    },
    bulk: {
        eyebrow: "// Bulk & institutional",
        titleLine1: "Bulk",
        titleLine2: "quote request",
        intro:
            "Placing an order for a lab group, institution, or research programme? Tell us what you need and our partner lab returns a costed quote — with clear lead times and volume pricing — within one business day.",
        tiersEyebrow: "Volume pricing",
        units: "units",
        leadTimesEyebrow: "Lead times",
        leadTimes: {
            standard: {
                label: "Standard — in stock",
                desc: "Ships from cold-chain stock within 24h of PO confirmation.",
            },
            priority: {
                label: "Priority batch",
                desc: "Reserved batch pull, quality-controlled and dispatched in 3–5 business days.",
            },
            flexible: {
                label: "Flexible / custom",
                desc: "Custom synthesis or large-batch reservation — 2–4 weeks with milestone updates.",
            },
        },
        institutionalEyebrow: "Institutional accounts",
        institutionalBody:
            "Universities, hospitals, and CROs can request net-30 terms, purchase-order billing, and a dedicated technical contact from our partner lab.",
        formTitle: "Request a bulk quote",
        name: "Contact name",
        institution: "Institution / lab",
        email: "Work email",
        phone: "Phone",
        country: "Country",
        leadTimeSelect: "Preferred lead time",
        compoundsLabel: "Compounds & quantities",
        compoundsHint: "Add each compound you need, with optional dose (e.g. 5mg) and unit count.",
        compoundPlaceholder: "Compound name (e.g. BPC-157)",
        dosePlaceholder: "Dose (opt.)",
        addCompound: "Add compound",
        removeRow: "Remove row",
        totalUnits: "Total units",
        notes: "Additional notes",
        notesPlaceholder: "Delivery deadlines, PO number, custom sequence details, or other context…",
        send: "Request quote",
        sending: "Sending…",
        success: "Quote request received. Our partner lab will reply with pricing and lead times within 1 business day.",
        errorNoItems: "Please add at least one compound with a quantity.",
        prefilledFromCart: "Prefilled from your current cart — adjust quantities as needed.",
        cartAvailable_one: "You have 1 item in your cart. Import it as the starting point?",
        cartAvailable_other: "You have {{count}} items in your cart. Import them as the starting point?",
        loadFromCart: "Load from cart",
    },

    about: {
        eyebrow: "// About Nexus",
        titleLine1: "We source",
        titleLine2: "the standard",
        titleAccent: "others",
        titleLine3: "chase.",
        intro:
            "Nexus Peptides is a distributor of premium research compounds. We partner exclusively with a synthesis lab whose biochemists refuse to compromise on purity — so researchers get the molecular precision they need to publish data they can defend.",
        standardsEyebrow: "// Standards",
        standardsTitleLine1: "Four commitments.",
        standardsTitleLine2: "Zero compromise.",
        commit: {
            molecular: { t: "Molecular precision", d: "Solid-phase synthesis with Fmoc protection, RP-HPLC purification to 99%+ purity." },
            verified: { t: "Third-party verification", d: "Every lot independently verified by accredited analytical chemistry labs." },
            coldChain: { t: "Cold chain integrity", d: "Lyophilized at −80°C, shipped in insulated containers with thermal monitoring." },
            coa: { t: "Transparent COAs", d: "Every product ships with a full Certificate of Analysis. No hidden data." },
        },
        missionEyebrow: "// Mission",
        missionTitle: "Why we exist",
        missionBody1:
            "Research is only as strong as its reagents. A flawed peptide doesn't just waste time — it can poison an entire literature, sending other labs chasing phantoms.",
        missionBody2:
            "Nexus exists to remove that variable. Every vial that leaves our partner lab is a quiet promise: the data it produces is the data your compound deserves.",
        statsEyebrow: "// By the numbers",
        stats: {
            founded: ["2019", "Founded"],
            compounds: ["42", "Active compounds"],
            purity: ["≥99%", "Median purity"],
            partners: ["1.2k+", "Research partners"],
            qc: ["6", "QC stages per lot"],
            dispatch: ["24h", "Cold chain dispatch"],
        },
        ctaTitle: "Build your research with the standard.",
        ctaExplore: "Explore catalog →",
    },
    research: {
        eyebrow: "// Knowledge base",
        titleLine1: "Research",
        titleAccent: "library",
        intro:
            "Curated peer-reviewed literature and internal white papers covering the compounds we synthesize. Updated continuously by our scientific advisory board.",
        statsStudies: "Indexed studies",
        statsCompounds: "Compounds covered",
        statsPartners: "Active research partners",
        partnershipEyebrow: "// Research partnership",
        partnershipTitle: "Collaborate with our partner lab",
        partnershipBody:
            "Academic and institutional researchers can apply for our compound subsidy program. Eligible projects receive up to 50% off list pricing for IRB-approved studies.",
        apply: "Apply for program",
    },
    contact: {
        eyebrow: "// Get in touch",
        titleLine1: "Talk to",
        titleLine2: "our partner lab.",
        intro:
            "Bulk orders, technical questions, COA requests, or research collaboration — our partner lab's scientific team responds within one business day.",
        infoEmail: "Email",
        infoPhone: "Phone",
        infoAddress: "Lab address",
        opsEyebrow: "Operations",
        ops:
            "Monday – Friday · 08:00 – 18:00 PT\nCold chain dispatch 24/7 for verified accounts.",
        formTitle: "Send a message",
        name: "Name",
        email: "Email",
        subject: "Subject",
        message: "Message",
        subjects: {
            general: "General inquiry",
            bulk: "Bulk order quote",
            coa: "COA request",
            collaboration: "Research collaboration",
            press: "Press / Media",
        },
        send: "Send message",
        sending: "Sending…",
        success: "Message received. Our team will respond within 1 business day.",
    },
    policy: {
        last: "Last updated:",
        backHome: "← Back to home",
    },
    ageGate: {
        eyebrow: "Age verification",
        titleLine1: "Are you 21 years",
        titleLine2: "or older?",
        body: "To enter this site you must confirm that you are at least 21 years of age and are accessing the catalogue strictly for in-vitro laboratory research purposes. Nexus Peptides products are not for human or veterinary consumption.",
        yes: "Yes — I'm 21 or older",
        no: "No — I'm under 21",
        storedNote: "Your selection is stored locally on this device.",
        deniedEyebrow: "Access restricted",
        deniedTitleLine1: "We're sorry —",
        deniedTitleLine2: "you can't enter.",
        deniedBody1: "Due to legal restrictions and applicable regulations, you must be ",
        deniedBodyEmphasis: "21 years of age or older",
        deniedBody2: " in order to purchase or browse peptides for research purposes. You are not permitted to access this site until you meet the minimum age requirement. Please come back when you do.",
        retryPrefix: "If you reached this screen by accident, you can ",
        retryLink: "go back and answer again",
        retrySuffix: ".",
    },
};
